/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/unbound-method */
/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Quick Links Service
 * Handles all API calls for Quick Action Links
 *
 * IMPORTANT: This service integrates directly with the QPilot QuickLinks API
 * No transformation layer - data flows directly between API and frontend
 * API uses numeric enums, UTC date fields, and specific naming conventions
 */

import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
} from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, of, throwError } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { environment } from "../../../../environments/environment";
import { QPilotAccessToken } from "../../../core/services/qpilot.service";
import {
  CreateQuickLinkRequest,
  CreateQuickLinkResponse,
  GenerateTokenRequest,
  GenerateTokenResponse,
  QuickLinkMetrics,
  SlugifyResponse,
  UpdateQuickLinkRequest,
  UpdateQuickLinkResponse,
  UpdateStatusRequest,
  UpdateStatusResponse,
} from "../interfaces/quick-link-api.interface";
import {
  PagedResult,
  QuickLinkApiResponse,
  QuickLinkListItem,
} from "../interfaces/quick-link-list-item.interface";
import { QuickLinksFilter } from "../interfaces/quick-links-filter.interface";

@Injectable({
  providedIn: "root",
})
export class QuickLinksService {
  // IMPORTANT: Using qpilotMockApiUrl temporarily for Roberto's dev environment
  // TODO: Switch to environment.qpilotApiUrl before production launch
  private readonly baseUrl = `${environment.qpilotApiUrl}`;

  constructor(private http: HttpClient) {
    console.log("🔗 QuickLinksService initialized with API:", this.baseUrl);
  }

  // ============================================================================
  // AUTHENTICATION HELPERS (matches QPilotService pattern exactly)
  // ============================================================================

  /**
   * Creates headers for the HTTP requests
   * EXACT COPY from QPilotService.createHeaders() - lines 320-338
   * @param accessToken Optional access token for authenticated requests
   * @returns HTTP headers
   */
  private createHeaders(accessToken?: QPilotAccessToken): HttpHeaders {
    let headers = new HttpHeaders();

    if (accessToken) {
      if (accessToken.TokenBearerAuth) {
        headers = headers.set(
          "Authorization",
          `Bearer ${accessToken.TokenBearerAuth}`,
        );
      } else if (accessToken.TokenAuth) {
        headers = headers.set(
          "Authorization",
          `Basic ${accessToken.TokenAuth}`,
        );
      }
    }

    return headers;
  }

  // ============================================================================
  // LIST & GET OPERATIONS
  // ============================================================================

  /**
   * Get paginated list of quick links with filters
   * API Endpoint: GET /Sites/{siteId}/Quicklinks
   *
   * Query Parameters:
   * - status (numeric): 0=Disabled, 1=Enabled, 2=Deleted
   * - actionType (numeric): 0=Resume, 1=Pause, 2=ProcessNow, 3=Reactivate
   * - usageLimitType (numeric): 0=SingleUse, 1=LimitedUse, 2=Unlimited
   * - search: Filter by name or slug
   * - page: Page number (default: 1)
   * - pageSize: Items per page (default: 25)
   * - orderBy: Field to sort by (e.g., "createdUtc", "name")
   * - order: "asc" or "desc"
   */
  getQuickLinks(
    filter: QuickLinksFilter,
    accessToken: QPilotAccessToken,
  ): Observable<PagedResult<QuickLinkListItem>> {
    const params = this.buildQueryParams(filter);

    return this.http
      .get<any>(`${this.baseUrl}/Sites/${filter.siteId}/Quicklinks`, {
        headers: this.createHeaders(accessToken),
        params,
      })
      .pipe(
        map((response) => this.transformApiResponse(response, filter)),
        catchError((error) => this.handleError(error)),
      );
  }

  /**
   * Get single quick link by ID
   * API Endpoint: GET /Sites/{siteId}/Quicklinks/{id}
   */
  getQuickLink(
    siteId: number,
    quickLinkId: number,
    accessToken: QPilotAccessToken,
  ): Observable<QuickLinkListItem> {
    return this.http
      .get<QuickLinkApiResponse>(
        `${this.baseUrl}/Sites/${siteId}/Quicklinks/${quickLinkId}`,
        {
          headers: this.createHeaders(accessToken),
        },
      )
      .pipe(
        map((response) => this.transformQuickLinkItem(response)),
        catchError((error) => this.handleError(error)),
      );
  }

  // ============================================================================
  // CREATE & UPDATE OPERATIONS
  // ============================================================================

  /**
   * Create new quick link
   * API Endpoint: POST /Sites/{siteId}/Quicklinks
   *
   * Request body must include:
   * - name: string
   * - slug: string (optional - auto-generated if omitted)
   * - actionType: 0-3 (numeric enum)
   * - usageLimitType: 0-2 (numeric enum)
   * - maxUses: number | null
   * - requiresLogin: boolean
   * - expiredUtc: string | null (ISO 8601)
   * - redirect: RedirectConfig object
   */
  createQuickLink(
    siteId: number,
    request: CreateQuickLinkRequest,
    accessToken: QPilotAccessToken,
  ): Observable<CreateQuickLinkResponse> {
    return this.http
      .post<CreateQuickLinkResponse>(
        `${this.baseUrl}/Sites/${siteId}/Quicklinks`,
        request,
        {
          headers: this.createHeaders(accessToken),
        },
      )
      .pipe(catchError((error) => this.handleError(error)));
  }

  /**
   * Update existing quick link
   * API Endpoint: PUT /Sites/{siteId}/Quicklinks/{id}
   *
   * Note: Slug cannot be changed after creation
   */
  updateQuickLink(
    siteId: number,
    quickLinkId: number,
    request: UpdateQuickLinkRequest,
    accessToken: QPilotAccessToken,
  ): Observable<UpdateQuickLinkResponse> {
    return this.http
      .put<UpdateQuickLinkResponse>(
        `${this.baseUrl}/Sites/${siteId}/Quicklinks/${quickLinkId}`,
        request,
        {
          headers: this.createHeaders(accessToken),
        },
      )
      .pipe(catchError((error) => this.handleError(error)));
  }

  /**
   * Update quick link status (enable/disable)
   * API Endpoint: PATCH /Sites/{siteId}/Quicklinks/{id}/Status
   *
   * Request body: { status: 0 | 1 | 2 }
   * 0 = Disabled, 1 = Enabled, 2 = Deleted
   */
  updateQuickLinkStatus(
    siteId: number,
    quickLinkId: number,
    request: UpdateStatusRequest,
    accessToken: QPilotAccessToken,
  ): Observable<UpdateStatusResponse> {
    return this.http
      .patch<UpdateStatusResponse>(
        `${this.baseUrl}/Sites/${siteId}/Quicklinks/${quickLinkId}/Status`,
        request,
        {
          headers: this.createHeaders(accessToken),
        },
      )
      .pipe(catchError((error) => this.handleError(error)));
  }

  // ============================================================================
  // DELETE OPERATION
  // ============================================================================

  /**
   * Delete quick link (soft delete - sets status to Deleted)
   * API Endpoint: DELETE /Sites/{siteId}/Quicklinks/{id}
   */
  deleteQuickLink(
    siteId: number,
    quickLinkId: number,
    accessToken: QPilotAccessToken,
  ): Observable<void> {
    return this.http
      .delete<void>(
        `${this.baseUrl}/Sites/${siteId}/Quicklinks/${quickLinkId}`,
        {
          headers: this.createHeaders(accessToken),
        },
      )
      .pipe(catchError((error) => this.handleError(error)));
  }

  /**
   * Permanently delete quick link (hard delete)
   * API Endpoint: DELETE /Sites/{siteId}/Quicklinks/{id}?permanent=true
   * Note: This permanently removes the quick link and all associated data.
   * Only available for quick links with status = Deleted (2)
   */
  permanentDeleteQuickLink(
    siteId: number,
    quickLinkId: number,
    accessToken: QPilotAccessToken,
  ): Observable<void> {
    const params = new HttpParams().set("permanent", "true");
    return this.http
      .delete<void>(
        `${this.baseUrl}/Sites/${siteId}/Quicklinks/${quickLinkId}`,
        {
          headers: this.createHeaders(accessToken),
          params,
        },
      )
      .pipe(catchError((error) => this.handleError(error)));
  }

  // ============================================================================
  // TOKEN GENERATION (Single-Use Links)
  // ============================================================================

  /**
   * Generate single-use token for a quick link
   * API Endpoint: POST /Sites/{siteId}/Quicklinks/{id}/Tokens
   *
   * Request body:
   * - scheduledOrderId: number
   * - expiredUtc: string | null (optional)
   *
   * Response includes:
   * - token: string (UUID - shown only once!)
   * - url: string (full URL with token)
   * - warning: string
   */
  generateToken(
    siteId: number,
    quickLinkId: number,
    request: GenerateTokenRequest,
    accessToken: QPilotAccessToken,
  ): Observable<GenerateTokenResponse> {
    return this.http
      .post<GenerateTokenResponse>(
        `${this.baseUrl}/Sites/${siteId}/Quicklinks/${quickLinkId}/Tokens`,
        request,
        {
          headers: this.createHeaders(accessToken),
        },
      )
      .pipe(catchError((error) => this.handleError(error)));
  }

  // ============================================================================
  // METRICS & ANALYTICS
  // ============================================================================

  /**
   * Get quick link metrics (usage statistics)
   * API Endpoint: GET /Sites/{siteId}/Quicklinks/{id}/Metrics
   *
   * Query Parameters:
   * - page: number (default: 1)
   * - pageSize: number (default: 25)
   * - startDate: ISO 8601 string (optional)
   * - endDate: ISO 8601 string (optional)
   */
  getQuickLinkMetrics(
    siteId: number,
    quickLinkId: number,
    accessToken: QPilotAccessToken,
    page = 1,
    pageSize = 25,
    startDate?: Date,
    endDate?: Date,
  ): Observable<QuickLinkMetrics> {
    let params = new HttpParams()
      .set("page", page.toString())
      .set("pageSize", pageSize.toString());

    if (startDate) {
      params = params.set("startDate", startDate.toISOString());
    }
    if (endDate) {
      params = params.set("endDate", endDate.toISOString());
    }

    return this.http
      .get<QuickLinkMetrics>(
        `${this.baseUrl}/Sites/${siteId}/Quicklinks/${quickLinkId}/Metrics`,
        {
          headers: this.createHeaders(accessToken),
          params,
        },
      )
      .pipe(catchError((error) => this.handleError(error)));
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  /**
   * Check if slug is unique (for form validation)
   * Note: This endpoint may not be implemented yet - returns true on error
   */
  checkSlugUniqueness(
    siteId: number,
    slug: string,
    accessToken: QPilotAccessToken,
    excludeId?: number,
  ): Observable<boolean> {
    let params = new HttpParams().set("slug", slug);
    if (excludeId) {
      params = params.set("excludeId", excludeId.toString());
    }

    return this.http
      .get<{ isUnique: boolean }>(
        `${this.baseUrl}/Sites/${siteId}/Quicklinks/check-slug`,
        {
          headers: this.createHeaders(accessToken),
          params,
        },
      )
      .pipe(
        map((response) => response.isUnique),
        catchError(() => {
          // Return true on error (optimistic approach)
          return of(true);
        }),
      );
  }

  /**
   * Slugify text and check availability (utility endpoint)
   * API Endpoint: POST /Sites/{siteId}/Quicklinks/Slugify
   *
   * Request body: { text: string }
   * Response: { originalText: string, slug: string, isAvailable: boolean, message?: string }
   */
  slugifyText(
    siteId: number,
    text: string,
    accessToken: QPilotAccessToken,
  ): Observable<SlugifyResponse> {
    return this.http
      .post<SlugifyResponse>(
        `${this.baseUrl}/Sites/${siteId}/Quicklinks/Slugify`,
        { text },
        {
          headers: this.createHeaders(accessToken),
        },
      )
      .pipe(catchError((error) => this.handleError(error)));
  }

  // ============================================================================
  // PRIVATE HELPER METHODS
  // ============================================================================

  /**
   * Transform API response to match frontend PagedResult interface
   * API returns: { items: T[], totalCount: number, totalPages: number }
   * Frontend expects: { data: T[], pagination: { currentPage, totalPages, totalItems, pageSize } }
   */
  private transformApiResponse(
    response: {
      items: QuickLinkApiResponse[];
      totalCount: number;
      totalPages: number;
    },
    filter: QuickLinksFilter,
  ): PagedResult<QuickLinkListItem> {
    console.log("🔧 Transforming API response:", response);

    // Transform each item to convert string enums to numeric enums
    const transformedItems = (response.items || []).map((item) =>
      this.transformQuickLinkItem(item),
    );

    const result: PagedResult<QuickLinkListItem> = {
      data: transformedItems,
      pagination: {
        currentPage: filter.page,
        totalPages: response.totalPages || 1,
        totalItems: response.totalCount || 0,
        pageSize: filter.pageSize,
      },
    };

    console.log("✅ Transformed result:", result);
    return result;
  }

  /**
   * Transform individual quick link item from API format to frontend format
   * Converts string enums to numeric enums
   */
  private transformQuickLinkItem(
    item: QuickLinkApiResponse,
  ): QuickLinkListItem {
    return {
      ...item,
      actionType: this.mapActionTypeToNumeric(item.actionType),
      status: this.mapStatusToNumeric(item.status),
      usageLimitType: this.mapUsageLimitTypeToNumeric(item.usageLimitType),
      redirect: item.redirect
        ? {
            ...item.redirect,
            type: this.mapRedirectTypeToNumeric(item.redirect.type),
          }
        : null,
    };
  }

  /**
   * Map action type string to numeric enum
   * "Resume" -> 0, "Pause" -> 1, "ProcessNow" -> 2, "Reactivate" -> 3
   */
  private mapActionTypeToNumeric(
    actionType: "Resume" | "Pause" | "ProcessNow" | "Reactivate",
  ): 0 | 1 | 2 | 3 {
    const map: Record<string, 0 | 1 | 2 | 3> = {
      Resume: 0,
      Pause: 1,
      ProcessNow: 2,
      Reactivate: 3,
    };
    return map[actionType] ?? 0;
  }

  /**
   * Map status string to numeric enum
   * "Disabled" -> 0, "Enabled" -> 1, "Deleted" -> 2
   */
  private mapStatusToNumeric(
    status: "Disabled" | "Enabled" | "Deleted",
  ): 0 | 1 | 2 {
    const map: Record<string, 0 | 1 | 2> = {
      Disabled: 0,
      Enabled: 1,
      Deleted: 2,
    };
    return map[status] ?? 0;
  }

  /**
   * Map usage limit type string to numeric enum
   * "SingleUse" -> 0, "LimitedUse" -> 1, "Unlimited" -> 2
   */
  private mapUsageLimitTypeToNumeric(
    usageLimitType: "Unlimited" | "LimitedUse" | "SingleUse",
  ): 0 | 1 | 2 {
    const map: Record<string, 0 | 1 | 2> = {
      SingleUse: 0,
      LimitedUse: 1,
      Unlimited: 2,
    };
    return map[usageLimitType] ?? 0;
  }

  /**
   * Map redirect type string to numeric enum
   * "ThankYouPage" -> 0, "V2Portal" -> 1, "CustomUrl" -> 2
   */
  private mapRedirectTypeToNumeric(
    redirectType: "ThankYouPage" | "V2Portal" | "CustomUrl",
  ): 0 | 1 | 2 {
    const map: Record<string, 0 | 1 | 2> = {
      ThankYouPage: 0,
      V2Portal: 1,
      CustomUrl: 2,
    };
    return map[redirectType] ?? 0;
  }

  /**
   * Build query parameters for list endpoint
   * Maps frontend filter object to API query parameters
   */
  private buildQueryParams(filter: QuickLinksFilter): HttpParams {
    let params = new HttpParams()
      .set("page", filter.page.toString())
      .set("pageSize", filter.pageSize.toString())
      .set("order", filter.order);

    // Search
    if (filter.search) {
      params = params.set("search", filter.search);
    }

    // Sort field
    if (filter.orderBy) {
      params = params.set("orderBy", filter.orderBy);
    }

    // Status filter (numeric enum array) - API expects "statuses" parameter
    if (filter.statuses && filter.statuses.length > 0) {
      filter.statuses.forEach((status) => {
        params = params.append("statuses", status.toString());
      });
    }

    // Action type filter (numeric enum array) - API expects "actionTypes" parameter
    if (filter.actionTypes && filter.actionTypes.length > 0) {
      filter.actionTypes.forEach((actionType) => {
        params = params.append("actionTypes", actionType.toString());
      });
    }

    // Usage limit type filter (numeric enum array) - currently unused
    if (filter.usageLimitType && filter.usageLimitType.length > 0) {
      filter.usageLimitType.forEach((usageLimitType) => {
        params = params.append("usageLimitType", usageLimitType.toString());
      });
    }

    return params;
  }

  /**
   * Handle HTTP errors
   * Log error details for debugging - toast notifications handled by interceptor
   *
   * Error codes (from API Phase 1):
   * - LINK_NOT_FOUND: Quick link not found
   * - LINK_DISABLED: Quick link is disabled
   * - LINK_EXPIRED: Quick link has expired
   * - USAGE_LIMIT_REACHED: Usage limit has been reached
   * - LOGIN_REQUIRED: Login is required
   * - INVALID_TOKEN: Invalid or expired token
   * - TOKEN_ALREADY_USED: Token has already been used
   * - TOKEN_EXPIRED: Token has expired
   */
  private handleError(error: HttpErrorResponse): Observable<never> {
    // Log detailed error for debugging
    console.error("🔴 Quick Links API Error:", {
      status: error.status,
      message: error.message,
      error: error.error,
    });

    // Return observable error (interceptor handles user notifications)
    return throwError(() => error);
  }
}
